import {
    deleteUser,
    login,
    userInfo,
    validatePromotionalToken,
    validateRecoveryToken,
} from "@/api/security";
import { logoutAndClean, setSession } from "@/utils/auth";

export default {
    // state como factory: forward-compatible con Vuex 4 / Pinia.
    state: () => ({
        user: {},
        token: "",
        company: {},
        recoveryToken: "",
        validToken: null,
        promotionalToken: "",
        validPromotionalToken: null,
        hasSIIAutomation: false,
        userInfoError: false,
    }),

    mutations: {
        SET_LOGIN: (state, payload) => {
            state.user = payload.user;
            state.token = payload.token;
            state.company = payload.user.companies[0];
        },
        SET_USER_INFO: (state, payload) => {
            state.user = payload;
        },
        SET_USER_INFO_ERROR: (state, payload) => {
            state.userInfoError = payload;
        },
        SET_RECOVERY_TOKEN: (state, payload) => {
            state.recoveryToken = payload;
        },
        SET_PROMOTIONAL_TOKEN: (state, payload) => {
            state.promotionalToken = payload;
        },
        SET_SII_AUTOMATION: (state, payload) => {
            state.hasSIIAutomation = payload;
        },
        SET_VALID_TOKEN: (state, payload) => {
            state.validToken = payload;
        },
        SET_VALID_PROMOTIONAL_TOKEN: (state, payload) => {
            state.validPromotionalToken = payload;
        },
        CLEAR_USER: (state) => {
            state.user = "";
            state.token = "";
            state.company = "";
        },
    },

    actions: {
        loginByUserInfo({ commit }, userInfo) {
            return new Promise((resolve, reject) => {
                login(userInfo.username, userInfo.password)
                    .then((response) => {
                        const data = response.data;
                        setSession(data);
                        commit("SET_LOGIN", data);
                        resolve(data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        setLoginInfo({ commit }, loginInfo) {
            commit("SET_LOGIN", loginInfo);
            if (loginInfo.user.companies[0]?.company_product_link_sii) {
                commit("SET_SII_AUTOMATION", true);
            } else {
                commit("SET_SII_AUTOMATION", false);
            }
        },
        // Payload objeto: las actions de Vuex reciben UN solo payload — la
        // firma posicional anterior (email, company_id) hacía que company_id
        // nunca llegara (dispatch lo trataba como `options`).
        // Reintenta con backoff ante fallas transitorias (timeout, 5xx, red):
        // sin esto, una sola request perdida dejaba user_info_loaded en false
        // para toda la sesión SPA (sidebar en skeleton hasta un F5 manual).
        // 4xx no se reintenta — es determinístico y el interceptor de api.js
        // ya maneja 401/403. Resuelve true/false en vez de rechazar para no
        // generar unhandled rejections en callers fire-and-forget (App.vue).
        async getUserInfo({ commit }, { email, companyId = null, retryDelaysMs = [1000, 3000] }) {
            for (let attempt = 0; attempt <= retryDelaysMs.length; attempt++) {
                try {
                    const response = await userInfo(email, companyId);
                    commit("SET_USER_INFO", response.data);
                    commit("SET_USER_INFO_ERROR", false);
                    return true;
                } catch (error) {
                    const status = error?.response?.status;
                    const retriable = !status || status >= 500;
                    if (!retriable || attempt === retryDelaysMs.length) {
                        commit("SET_USER_INFO_ERROR", true);
                        return false;
                    }
                    await new Promise((resolve) =>
                        setTimeout(resolve, retryDelaysMs[attempt])
                    );
                }
            }
        },
        async clearUserInfo({ commit }) {
            // La navegación a "/" la hace el caller (vista/componente); el store
            // sólo limpia la sesión. Mantiene al router fuera de la capa de estado.
            try {
                await logoutAndClean();
            } finally {
                commit("CLEAR_USER");
            }
        },
        validateRecoveryToken({ commit }, token) {
            return new Promise((resolve, reject) => {
                validateRecoveryToken(token)
                    .then(() => {
                        commit("SET_VALID_TOKEN", true);
                        resolve(true);
                    })
                    .catch(() => {
                        commit("SET_VALID_TOKEN", false);
                        reject(false);
                    });
            });
        },
        callValidatePromotionalToken({ commit }, token) {
            return new Promise((resolve, reject) => {
                validatePromotionalToken(token)
                    .then(() => {
                        commit("SET_VALID_PROMOTIONAL_TOKEN", true);
                        resolve(true);
                    })
                    .catch(() => {
                        commit("SET_VALID_PROMOTIONAL_TOKEN", false);
                        reject(false);
                    });
            });
        },
        async deleteSelf({ commit }) {
            // Igual que clearUserInfo: la navegación queda en el caller.
            try {
                await deleteUser();
                await logoutAndClean();
            } finally {
                commit("CLEAR_USER");
            }
        },
    },
    namespaced: true,
};
